import PropTypes from 'prop-types';
import React from 'react';
import { merge, sortBy, toLower } from 'lodash';
import api from '../../../api/Entities.api';
import reduxStore from './../../../reduxStore';
import categoriesApi from './../../../api/Categories.api';
import { injectIntl, FormattedMessage } from 'react-intl';
import { SORT, FILTER_TYPE_DROPDOWN, ENTITIES_PAGE_SIZE } from './../../../constants';
import EntityCategoriesPillList from "./../../../pages/Entities/components/EntityCategoriesPillList";
import { BoldedCell } from '../Components';
import TableCoreComponent from './../TableCoreComponent';
import withInfiniteScroll from './../hoc/Scrollable';
import withSort from './../hoc/Sortable';
import withSearch from './../hoc/Searchable';
import withSelection from './../hoc/Selectable';
import LightTable from './../VeryLightTable';
import utils from './../../../utils/utilities';
import Spinner from "../../Spinner/Spinner";
import { compose } from 'redux';

const Table = compose(
    withSelection,
    withSort,
    withSearch,
    withInfiniteScroll
)(LightTable);

const defaultLoadParams = {
    filters: { query: "", categoryIdFilter: "defaultAllCategories", active: "active" },
    sorting: { sortBy: "companyTitleSort", direction: SORT.ASC },
    pagination: { pageNumber: 0, pageSize: ENTITIES_PAGE_SIZE },
    reset: false
};

class SpecificEntitiesTable extends TableCoreComponent {
    static propTypes = {
        onChange: PropTypes.func.isRequired,
        entityIds: PropTypes.arrayOf(PropTypes.string)
    };

    static defaultProps = {
        entityIds: null
    };
    constructor(props) {
        super(props);
        this.state = {
            categories: {},
            listItems: null,
            currentPage: 0,
            totalCount: 0,
            filterCount: 0,
            loadParams: defaultLoadParams,
            selectedItems: [],
            searchTerm: ""
        };

        this.defaultLoadParams = this.loadParams = defaultLoadParams;
        this.api = api;

        this.loadItems = this.loadItems.bind(this);
        this.handleErrorState = this.handleErrorState.bind(this);
        this.onChange = this.onChange.bind(this);
        this.selectAllToggle = this.selectAllToggle.bind(this);
    }

    componentDidMount() {
        categoriesApi.getCategoryNames().then((categories) => {
            this.setState({ categories });
            const categoryIdFilter = utils.getSingleCategoryFilter(reduxStore.getState().user.preferences.filters.entitiesFilter, categories);

            if (this.props.entityIds && this.props.entityIds.length) {
                return api.specific(this.props.entityIds).then(entities => {
                    this.setState({
                        selectedItems: entities.map(entity => this.formatItem(entity))
                    });
                    this.loadItems({ filters: { categoryIdFilter } });
                }).catch((e) => this.showGenericError(e, true));
            } else {
                this.loadItems({ filters: { categoryIdFilter } });
            }
        }).catch(this.showGenericError);
    }

    /**
     * View function for item in selected box
     * @param item
     */
    formatSelectedItem = (item) => <span>{item.companyTitle}</span>;

    /**
     * Handler for selection change
     * @param selectedItems
     */
    onChange = (selectedItems) => {
        this.setState({ selectedItems });
        this.props.onChange(selectedItems.map(item => item.object.id));
    };

    selectAllToggle() {
        if (this.state.selectedItems.length === this.state.totalCount) {
            this.onChange([])
        } else {
            api.get(defaultLoadParams.filters, defaultLoadParams.sorting, { page: -1, pageSize: -1 }).then(items => {
                const allItems = items.body;
                if (allItems.length) {
                    this.onChange(allItems.map(item => this.formatItem(item)));
                }
            })
        }
    }

    handleSelectionChange(selectedItems) {
        this.onChange(selectedItems);
    }

    handleChangeSearchTerm(searchTerm) {
        this.setState({ searchTerm })
    }

    headerItems = [
        {
            key: "companyTitle",
            label: <FormattedMessage id={"Entities_Review_Error_errorCompanies.CompanyName"}/>
        },
        {
            key: "customerCompanyId",
            label: <FormattedMessage id='Entities_Edit_UIText_thirdPartyListing.thirdPartyIdColumnHeading'/>
        },
        {
            key: "companyName",
            label: <FormattedMessage id='Entities_Review_UIText_reviewCompanies.CompanyNames'/>
        },
        {
            key: "contextKeywords",
            label: <FormattedMessage id='Entities_Review_UIText_reviewCompanies.contextKeywords'/>
        },
        {
            key: "exclusionKeywords",
            label: <FormattedMessage id='Entities_Review_UIText_reviewCompanies.excludedKeywords'/>
        },
        {
            key: "active",
            label: <FormattedMessage id={"Entities_Edit_UIText_thirdPartyListing.statusColumnHeading"}/>
        },
        {
            key: "categoryIDs",
            label: <FormattedMessage id={"Entities_Edit_UIText_thirdPartyListing.categoriesColumnHeading"}/>,
        }
    ];

    /**
     * View function for items in table
     * @param item
     * @param searchTerm
     * @returns {{key, companyTitle: XML, active: string, duns: (string|string|*), categoryIDs: XML, object: *}}
     */
    formatItem = (item, searchTerm = "") => {
        return {
            key: item.id,
            companyTitle: <BoldedCell value={item.companyTitle} searchTerm={searchTerm} />,
            customerCompanyId: <BoldedCell value={item.customerCompanyId} searchTerm={searchTerm} />,
            companyName: <BoldedCell value={item.companyName.join(', ')} searchTerm={searchTerm} />,
            contextKeywords: <BoldedCell value={item.contextKeywords.join(", ")} searchTerm={searchTerm}/>,
            exclusionKeywords: <BoldedCell value={item.exclusionKeywords.join(", ")} searchTerm={searchTerm}/>,
            active: <span className={"label " + (item.active ? "active" : "inactive")}>
                <FormattedMessage id={"General_CoreFunctionality_UIText_general." + (item.active ? "active" : "inactive")}/>
            </span>,
            categoryIDs: <Spinner isLoading={this.state.categories === null}>
                <EntityCategoriesPillList categories={this.getCategories(item.categoryIDs)}
                                          entity={item} />
            </Spinner>,
            object: item
        }
    };

    getCategories(categoryIDs) {
        if (this.state.categories) {
            return categoryIDs.map(categoryID => {
                if (this.state.categories[categoryID]) {
                    return { id: categoryID, label: this.state.categories[categoryID] }
                } else {
                    console.log("ERROR -- Category label not found in list");
                    this.updateCategoriesName();
                    return { id: null, label: "[Not found]" };
                }
            })
        } else {
            return []
        }
    }

    getFilters() {
        let categories = [{
            label: this.props.intl.formatMessage({ id: "Entities_Review_UIText_reviewCompanies.allCategories" }),
            value: "defaultAllCategories"
        }];
        if (this.state.categories) {
            categories = [];
            for (let id in this.state.categories) {
                categories.push({
                    label: this.state.categories[id],
                    value: id
                });
            }
            categories = categories.sort((a, b) => toLower(a.label) > toLower(b.label) ? 1 : -1)
            categories = [
                {
                    label: this.props.intl.formatMessage({ id: "Entities_Review_UIText_reviewCompanies.allCategories" }),
                    value: "defaultAllCategories"
                }, ...categories ];
        }
        return [
            {
                type: FILTER_TYPE_DROPDOWN,
                label: <FormattedMessage id={"Entities_Review_UIText_reviewCompanies.categories"}/>,
                field: "categoryIdFilter",
                options: categories,
                value: this.loadParams.filters.categoryIdFilter,
                default: "defaultAllCategories",
                onChange: utils.setCategoryPersistenceFilter
            }
        ]
    }

    render() {
        return <div className='selectable-table specific-entities-table'>
            <Table {...this.getTableProps()}
                   selectedItems={sortBy(this.state.selectedItems, item => item.companyTitle.props.value.toLowerCase())}
                   filters={this.getFilters()}/>
        </div>
    }
}

export default injectIntl(SpecificEntitiesTable);


// WEBPACK FOOTER //
// ./src/scripts/reusable/LightTable/specific/SpecificEntitiesTable.jsx